import { useRef, useState } from 'react';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';

/**
 * Drag-drop / click upload. Creates the documents/{docId} record AND uploads
 * to Storage at uploads/{entityId}/{docId}/{filename}. The Storage trigger
 * picks it up from there.
 */
export function UploadButton() {
  const { user } = useAuth();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dropping, setDropping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(files: FileList | File[]) {
    if (!user) return;
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const docRef = doc(db, 'documents', randomId());
        const path = `uploads/unassigned/${docRef.id}/${sanitize(file.name)}`;
        const objRef = storageRef(storage, path);
        await uploadBytes(objRef, file, {
          contentType: file.type || 'application/octet-stream',
          customMetadata: {
            uploadedBy: user.uid,
            source: 'upload',
            category: 'other', // tentative; classify step rewrites
          },
        });
        // Pre-create the doc record so the user sees it in the list immediately.
        await setDoc(docRef, {
          id: docRef.id,
          storagePath: path,
          originalName: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          uploadedBy: user.uid,
          uploadedAt: serverTimestamp(),
          source: { type: 'upload' },
          entityIds: [],
          category: 'other',
          docType: 'uncategorized',
          parties: [],
          status: 'uploaded',
          visibility: { allowedRoles: ['admin'] },
          version: 1,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDropping(true);
      }}
      onDragLeave={() => setDropping(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDropping(false);
        if (e.dataTransfer.files?.length) void upload(e.dataTransfer.files);
      }}
      className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 transition-colors ${
        dropping ? 'border-stone-900 bg-stone-100' : 'border-stone-300 bg-white'
      }`}
    >
      <div className="text-sm text-stone-600">
        Drag files here, or{' '}
        <button
          type="button"
          className="font-medium text-stone-900 underline"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
        >
          browse
        </button>
      </div>
      <div className="text-xs text-stone-400">
        PDF · DOCX · XLSX · TXT · images, up to 100 MB
      </div>
      <input
        ref={fileInput}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => e.target.files && void upload(e.target.files)}
      />
      {busy && <div className="text-xs text-stone-500">Uploading…</div>}
      {error && <div className="text-xs text-red-700">{error}</div>}
    </div>
  );
}

function randomId(): string {
  return `doc_${crypto.randomUUID().replace(/-/g, '').slice(0, 22)}`;
}

function sanitize(filename: string): string {
  return filename.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 200);
}
